import { create } from '@bufbuild/protobuf'
import type {
  Supply48DataDay,
  Supply48DataSource,
} from '@ebalance/proto/bffuser/v1/supply_48_data_model_pb'
import {
  DateSchema,
  type Date as ProtoDate,
} from '@ebalance/proto/google/type/date_pb'

import type { EditRowState } from '~/pages/Supply48Data/models/editState'
import {
  isKomaEditing,
  makeEditRowKey,
} from '~/pages/Supply48Data/models/editState'
import type { DayIndex } from '~/pages/Supply48Data/models/saveRows'

/**
 * ProtoDate を "YYYY-MM-DD" の文字列キーに変換する。
 * Grid 内の行キー / 保存対象日の判定キーとして使用する。
 */
export function toDateKey(date: ProtoDate): string {
  return `${date.year}-${String(date.month).padStart(2, '0')}-${String(date.day).padStart(2, '0')}`
}

/**
 * フィルタ期間 (JS Date) 内の全 ProtoDate 一覧を生成する。
 * データのない日も行として表示し、新規入力 → Save を可能にする。
 *
 * §9: Grid の buildDateRange を models/gridRows.ts に移動。
 * proto Date 生成は canonical create(DateSchema) を使用。
 */
export function buildDateRange(
  filterDateFrom: Date | undefined,
  filterDateTo: Date | undefined,
): ProtoDate[] {
  if (filterDateFrom === undefined || filterDateTo === undefined) {
    return []
  }

  const result: ProtoDate[] = []
  const current = new Date(filterDateFrom)
  current.setHours(0, 0, 0, 0)
  const end = new Date(filterDateTo)
  end.setHours(0, 0, 0, 0)

  while (current <= end) {
    result.push(
      create(DateSchema, {
        year: current.getFullYear(),
        month: current.getMonth() + 1,
        day: current.getDate(),
      }),
    )
    current.setDate(current.getDate() + 1)
  }

  return result
}

/**
 * 各 leg の日付データを rowKey → Supply48DataDay にインデックスする (dayIndex)。
 * §2: page で 1 回だけ useMemo 構築する。
 */
export function buildDayIndex(sources: Supply48DataSource[]): DayIndex {
  const map: DayIndex = new Map()
  for (const source of sources) {
    for (const day of source.days) {
      if (day.deliveryDate !== undefined) {
        const key = makeEditRowKey(
          source.supply48SourceId,
          day.deliveryDate.year,
          day.deliveryDate.month,
          day.deliveryDate.day,
        )
        map.set(key, day)
      }
    }
  }
  return map
}

/**
 * §9 Chart/Grid 共通: 1 コマの表示値を計算する純関数。
 * 編集値があれば優先、なければ元の slot 値。
 *
 * サーバー値 (day.slots) が未設定の場合は 0 にフォールバックする
 * (AC-S4D-050 / AC-S4D-053 ※)。48 点データ画面は「未入力（ブランク）」
 * ステータスを持たず、コマは常に数値で表示する方針のため、#5131 反転前に
 * 登録済みの既存契約・GC 済み据え置きコマ（恒久的に DB 値が null のまま残る）
 * も 0 として描画する（表示層 null→0 fallback。DB 側は migration しない）。
 *
 * 編集中に明示的にクリアした値 (editedValue = null) はこの fallback の対象
 * 外で null をそのまま返す。クリアは未保存の編集セッション中の状態であり、
 * 本 fallback が指す「保存済みデータの据え置き表示」とは別の関心事のため。
 */
export function displaySlotValue(
  day: Supply48DataDay | undefined,
  editSlots: Array<number | null | undefined> | undefined,
  komaIndex: number,
): number | null {
  const editedValue = editSlots?.[komaIndex]
  if (editedValue !== undefined) {
    return editedValue
  }
  const slotValue = day?.slots[komaIndex]?.value
  return slotValue ?? 0
}

/**
 * displaySlotValue と同じ優先順位 (編集値 → サーバー値) だが、未設定サーバー
 * 値を 0 にフォールバックしない「素の」値を返す (= AC-S4D-050 以前の
 * displaySlotValue と同じ計算)。
 *
 * Escape (focus 時点の値へ戻す、useSupply48GridKeyboard.ts) の復元元に使う。
 * displaySlotValue の 0 fallback は表示専用の演出であり、フォーカス時点で
 * セルが未編集 (editedValue === undefined) だった場合、Escape は「何も
 * 変更しなかった」ことにしたい。ここで表示値 (0) を復元すると、
 * recomputeRowState (editState.ts) が保存値 null と比較して不一致
 * (0 !== null) と判定し、未編集セルが dirty のまま保存対象になり、
 * サーバー値が意図せず 0 に書き換わる (#7689 の bot 指摘)。
 */
export function underlyingSlotValue(
  day: Supply48DataDay | undefined,
  editSlots: Array<number | null | undefined> | undefined,
  komaIndex: number,
): number | null {
  const editedValue = editSlots?.[komaIndex]
  if (editedValue !== undefined) {
    return editedValue
  }
  return day?.slots[komaIndex]?.value ?? null
}

/**
 * **CSV に載らない (= 未保存の) コマ数**を数える。
 *
 * AC-S4D-007 のダウンロード確認ダイアログが出す
 * 「保存済みの値だけを出力します。編集中の {{count}} コマは反映されません。」の {{count}}。
 *
 * `countManualEdits` (`manualEditCount.ts`) と**別の量**なので流用しないこと。
 * あちらは AC-S4D-008「自動計算に戻す」の対象数 = **手動変更コマ**で、
 * 保存後も残る層 (サーバー応答の `manualEditKomas` を含む)。混同すると
 * - 過大: 保存済みの手動編集が 10 コマある状態で 1 コマ編集 → 「編集中の 11 コマ」 (実際は 1)
 * - 過小: 自動計算値スナップショットを持たない行だけ編集 → 「編集中の 0 コマ」 (Alert は出る)
 * となる (issue #6382 / PR #6420 のレビュー指摘)。
 *
 * design プロト `supply/48data.html` も 2 層を分けて持っており、本関数は
 * `edits` (保存時に現 context 分を delete する未保存レイヤ) 側に対応する。
 * `autoBase` (保存後も残る手動変更レイヤ) が `countManualEdits` 側。
 *
 * コマごとの判定はグリッドの「編集中」表示 (AC-S4D-049) と同じ `isKomaEditing` を
 * 使い、サーバー値を `day.slots` から**その場で**引き直す。`recomputeRowState` は
 * 書き込んだ行 (touchedKeys) にしか走らないため、`slots` の undefined 化がいつ
 * 行われたかに依存させない。保存で `dirty` が false に落ちた行は数えない
 * (保存済み = CSV に載る)。
 */
export function countUnsavedKomas(
  editState: ReadonlyMap<
    string,
    Pick<EditRowState, 'slots' | 'dirty' | 'legId' | 'manualEditKomas'>
  >,
  dayIndex: DayIndex,
  nonStandardLegIds: ReadonlySet<string>,
): number {
  let count = 0
  for (const [key, row] of editState.entries()) {
    const day = dayIndex.get(key)
    for (const index of row.slots.keys()) {
      // 未設定のサーバー値は null 扱い (`recomputeRowState` と同一規則)
      if (
        isKomaEditing(
          row,
          day?.slots[index]?.value ?? null,
          index + 1,
          nonStandardLegIds.has(row.legId)
            ? (day?.manualEditKomas ?? [])
            : undefined,
        )
      ) {
        count++
      }
    }
  }
  return count
}
